import EntityService from '../../api/entity';
import PartnershipsService from 'src/api/partnerships';
import { useDrilldownV2 } from 'src/services/drilldown/routing';
import Vue from 'vue';

// initial state
const state = {
  entityDrillDownConfig: {
    visible: false,
    entityType: '',
    entitySubType: '',
    entityId: [],
    entityName: [],
    query: null,
    queryScope: true,
    hideQueryScopeToggle: false,
    yearRange: null,
    historyIndex: null,
    source: null,
    documentFilters: {},
    tab: 'documents',
    showAdvisories: false,
  },
  entityMetaData: null,
  entityAnalytics: null,
  entityAssociations: null,
  entityNetworkGraphData: null,
  entityBlueLensData: null,
  metaDataLoading: false,
  analyticsLoading: false,
  associationsLoading: false,
  networkGraphDataLoading: false,
  blueLensDataLoading: false,
  entityDrillDownHistory: [],
};

const getters = {
  entityMetaData: (state) => {
    return state.entityMetaData;
  },
  entityAnalytics: (state) => {
    return state.entityAnalytics;
  },
  entityAssociations: (state) => {
    return state.entityAssociations;
  },
  entityDrillDownConfig: (state) => {
    return state.entityDrillDownConfig;
  },
  metaDataLoading: (state) => {
    return state.metaDataLoading;
  },
  analyticsLoading: (state) => {
    return state.analyticsLoading;
  },
  associationsLoading: (state) => {
    return state.associationsLoading;
  },
  entityDrillDownHistory: (state) => {
    return state.entityDrillDownHistory;
  },
  entityDocumentFilters: (state) => {
    return state.entityDrillDownConfig.documentFilters;
  },
  entityNetworkGraphData: (state) => {
    return state.entityNetworkGraphData;
  },
  networkGraphDataLoading: (state) => {
    return state.networkGraphDataLoading;
  },
  entityBlueLensData: (state) => {
    return state.entityBlueLensData;
  },
  blueLensDataLoading: (state) => {
    return state.blueLensDataLoading;
  },
};

const actions = {
  async getEntityData({ dispatch, state, rootGetters }, payload) {
    if (payload) {
      // Set scan type for search service -- get from Scans module, default to semantic if not set, i.e., if this action is dispatched from outside the scan context
      payload.scan_type = rootGetters['scans/scanType']
        ? rootGetters['scans/scanType']
        : 'boolean';
      // Set source for elasticsearch index by what is included in the payload --OR-- whatever the source is in the drill down config
      payload.source = payload.source
        ? payload.source
        : state.entityDrillDownConfig.source;
      dispatch('getMetaData', payload);
      dispatch('getAssociations', payload);
      dispatch('getAnalytics', payload);
      dispatch('getNetworkGraphData', payload);
      dispatch('getBlueLensData', payload);
    }
  },

  async getMetrics({ commit }, payload) {
    const entityService = new EntityService();
    const response = await entityService.getMetrics(payload);
    if (response && response.success) {
      commit('setEntityMetrics', response.data);
    }
  },

  // Clear all entity data
  clearEntityData({ commit }) {
    commit('setEntityMetaData', null);
    commit('setEntityAnalytics', null);
    commit('setEntityNetworkGraphData', null);
    commit('setEntityBlueLensData', null);
  },

  // Use EntityService to send entityType and entityID to entity service to return the metadata for a given entity
  async getMetaData({ commit }, payload) {
    const entityService = new EntityService();
    const response = await entityService.getEntityMetaData(payload);
    if (response && response.data) {
      commit('setEntityMetaData', response.data);
    } else {
      commit('setEntityMetaData', {});
    }
    commit('setMetaDataLoading', false);
  },

  // Use EntityService to send entityType, entityID, and query payload to entity service to return the analytics data for a given entity
  async getAnalytics({ commit }, payload) {
    // bpl and sanction entities do not have analytics data
    if (payload.entityType !== 'bluelens' && payload.source !== 'sanction') {
      const entityService = new EntityService();
      const response = await entityService.getEntityAnalytics(payload);
      if (response && response.data) {
        commit('setEntityAnalytics', response.data);
        commit('setAnalyticsLoading', false);
      }
    } else {
      commit('setAnalyticsLoading', false);
      commit('setEntityAnalytics', null);
    }
  },

  // Use EntityService to get entity associations for the drilldown filter lists
  async getAssociations({ commit }, payload) {
    // bpl and sanction entities do not have associations
    if (payload.entityType !== 'bluelens' && payload.source !== 'sanction') {
      const entityService = new EntityService();
      const response = await entityService.getEntityAssociations(payload);
      if (response && response.data) {
        commit('setEntityAssociations', response.data);
        commit('setAssociationsLoading', false);
      }
    } else {
      commit('setAssociationsLoading', false);
      commit('setEntityAssociations', null);
    }
  },

  // Use PartnershipsService to retrieve network graph data for a given entity
  async getNetworkGraphData({ commit }, payload) {
    const entityTypesWithCollaborations = ['author', 'institution'];
    if (
      payload &&
      payload.entityId.length === 1 &&
      entityTypesWithCollaborations.includes(payload.entityType)
    ) {
      // Build the filters object based on the payload, adding the year range filter if it exists
      let filters = {};
      if (payload.filters) {
        if (payload.source.toLowerCase().includes('publication')) {
          // publication and fundingPublication sources filter by publication year
          filters.publication_year = [payload.filters];
        } else if (payload.source === 'patent') {
          // patent source filters by filing year
          filters.filing_year = [payload.filters];
        }
      }
      const _payload = {
        entityId: payload.entityId,
        type: payload.entityType,
        source: payload.source,
        subType: payload.entitySubType,
        scan_type: payload.scan_type,
        filters: filters,
        query: payload.query || null,
      };
      const partnershipsService = new PartnershipsService();
      const response = await partnershipsService.getEntityPartnerships(
        _payload
      );
      if (response && response.networkGraphData) {
        commit('setEntityNetworkGraphData', response.networkGraphData);
        commit('setNetworkGraphDataLoading', false);
      }
    }
  },
  async getBlueLensData({ commit, state }, payload) {
    const entityTypesWithBlueLensData = ['institution', 'funder', 'bluelens'];
    const sourcesWithBlueLensData = [
      'publication',
      'fundingPublication',
      'bluelens',
    ];
    // Check if the payload is for a single entity (or a merged set of duplicate hits), the source and entity type
    // have blue lens data, and blue lens data is not already loaded
    if (
      payload &&
      (payload.entityId.length === 1 || payload.mergeMode) &&
      sourcesWithBlueLensData.includes(payload.source) &&
      entityTypesWithBlueLensData.includes(payload.entityType) &&
      !state.entityBlueLensData
    ) {
      commit('setBlueLensDataLoading', true);
      const entityService = new EntityService();
      const response = await entityService.getBlueLensData(payload);
      if (response && response.data) {
        commit('setEntityBlueLensData', response.data);
      }
      commit('setBlueLensDataLoading', false);
    }
  },
  setEntityDrillDownConfig({ commit, dispatch, state, rootGetters }, payload) {
    commit('setAnalyticsLoading', true);
    commit('setAssociationsLoading', true);
    commit('setMetaDataLoading', true);
    commit('setNetworkGraphDataLoading', true);

    // Reset entity document filters if no payload or payload is for a different entity
    if (
      !payload ||
      payload.entityId[0] !== state.entityDrillDownConfig.entityId[0] ||
      payload.entityId.length !== state.entityDrillDownConfig.entityId.length
    ) {
      dispatch('resetEntityDocumentFilters', null);
    }

    // Update history before setting new config as we need the index of the currently-loaded config. We clear history if no payload (drill down closed), add to history if not yet present (no historyIndex), else no update needed (loaded from history).
    if (!payload || payload.historyIndex == null) {
      commit('updateHistory', payload);
    }

    // Clear stale entity data so it doesn't render while new data loads
    commit('setEntityMetaData', null);
    commit('setEntityBlueLensData', null);

    // Load up new data
    commit('setEntityDrillDownConfig', payload);
    // Reset pagination when the config changes
    dispatch('documents/resetEntityPagination', null, { root: true });

    if (
      payload &&
      useDrilldownV2(state.entityDrillDownConfig, rootGetters['app/featureFlags'], rootGetters['drilldown/classic'])
    ) {
      // The v2 drilldown loads its own data. Clear the legacy loading flags so they don't disable Back/Forward.
      commit('setAnalyticsLoading', false);
      commit('setAssociationsLoading', false);
      commit('setMetaDataLoading', false);
      commit('setNetworkGraphDataLoading', false);
      dispatch('drilldown/open', state.entityDrillDownConfig, { root: true });
    } else if (!payload) {
      // Drilldown closed: the next v2 open starts from default filters
      dispatch('drilldown/reset', null, { root: true });
    } else {
      // Payload is present, load entity drill down data and docs
      dispatch('getEntityData', payload);
      // bpl and sanction entities do not display documents in the drilldown
      if (payload.entityType !== 'bluelens' && payload.source !== 'sanction') {
        // Dispatch an action from the documents module to retrieve documents for this specific entity
        dispatch('documents/fetchEntityDocuments', payload, { root: true });
      }
    }
  },
  // Reload the current drilldown in place, e.g. after switching between the classic and v2 person views. The config already has a historyIndex, so no history entry is added.
  reloadCurrent({ state, dispatch }) {
    if (state.entityDrillDownConfig.visible) {
      dispatch('setEntityDrillDownConfig', { ...state.entityDrillDownConfig });
    }
  },
  loadHistory({ state, dispatch }, payload) {
    // Get current history index
    const currHistoryIndex = state.entityDrillDownConfig.historyIndex;

    // If payload is 'back' and we're not at the beginning of the history, load the previous entity drill down config
    if (payload === 'back' && currHistoryIndex > 0) {
      const prevEntity =
        state.entityDrillDownHistory[currHistoryIndex - 1] || null;
      if (prevEntity) {
        dispatch('setEntityDrillDownConfig', prevEntity);
      }
      // If payload is 'forward' and we're not at the end of the history, load the next entity drill down config
    } else if (
      payload === 'forward' &&
      currHistoryIndex < state.entityDrillDownHistory.length - 1
    ) {
      const nextEntity =
        state.entityDrillDownHistory[currHistoryIndex + 1] || null;

      if (nextEntity) {
        dispatch('setEntityDrillDownConfig', nextEntity);
      }
    }
  },
  setEntityDocumentFilters({ commit, dispatch, state }, payload) {
    const drillDownConfig = state.entityDrillDownConfig;
    if (payload) {
      const { filterType, filters, action } = payload;
      let documentFilters = {};
      // Process the filters based on the action
      if (action === 'remove') {
        // Remove the filters from the document filters
        documentFilters = {
          ...drillDownConfig.documentFilters,
          [filterType]: drillDownConfig.documentFilters[filterType].filter(
            (_filter) => !filters.some((f) => f.id === _filter.id)
          ),
        };
        // Remove the filter type if its array is empty
        if (documentFilters[filterType].length === 0) {
          delete documentFilters[filterType];
        }
      } else {
        // Add the new filters to the document filters
        documentFilters = {
          ...drillDownConfig.documentFilters,
          [filterType]: drillDownConfig.documentFilters[filterType]
            ? drillDownConfig.documentFilters[filterType].concat(filters) // If the filter type exists, add the new filters to the array
            : [...filters], // If the filter type doesn't exist, create a new array with the new filters
        };
      }
      // Commit mutation to set the new document filters in the store
      commit('setEntityDocumentFilters', documentFilters);
      // Update history with new filters
      commit('updateHistory', {
        documentFilters,
        entityId: drillDownConfig.entityId,
        entityType: drillDownConfig.entityType,
      });
    } else {
      // Filters have been cleared
      // Commit mutation to clear the document filters in the store
      commit('setEntityDocumentFilters', null);
    }
    // Dispatch action to reset entity pagination
    dispatch('documents/resetEntityPagination', null, { root: true });
    // Dispatch an action from the documents module to retrieve documents for the updated filters
    dispatch('documents/fetchEntityDocuments', null, { root: true });
  },
  resetEntityDocumentFilters({ commit }) {
    // Commit mutation to clear the document filters in the store
    commit('setEntityDocumentFilters', null);
  },
  setEntityDrillDownTab({ commit, state }, payload) {
    commit('setEntityDrillDownTab', payload);
    commit('updateHistory', {
      entityId: state.entityDrillDownConfig.entityId,
      entityType: state.entityDrillDownConfig.entityType,
      tab: payload,
    });
  },
  setShowAdvisories({ commit }, payload) {
    commit('setShowAdvisories', payload);
  },
};

const mutations = {
  setEntityMetaData(state, payload) {
    state.entityMetaData = payload;
  },
  setEntityAnalytics(state, payload) {
    state.entityAnalytics = payload;
  },
  setEntityAssociations(state, payload) {
    state.entityAssociations = payload;
  },
  setEntityDrillDownConfig(state, payload) {
    // No payload = reset to default state, i.e., close the dialog
    if (!payload) {
      state.entityDrillDownConfig = {
        visible: false,
        entityType: '',
        entitySubType: '',
        entityId: [],
        entityName: [],
        query: null,
        queryScope: true,
        hideQueryScopeToggle: false,
        yearRange: null,
        source: null,
        documentFilters: {},
        tab: 'documents',
        showAdvisories: false,
      };
      state.entityAnalytics = null;
      state.entityMetaData = null;
      state.entityNetworkGraphData = null;
      state.entityBlueLensData = null;
    } else {
      // For some drill down types, the payload contains filters with a min and max year range. We want to set yearRange and filters config to these values if they exist, else clear them so they don't persist to other drilldowns.
      payload.yearRange = payload.filters = payload.filters
        ? payload.filters
        : null;
      // If query scope is not present in the payload, set it to true if a query is present, else false
      payload.queryScope = payload.queryScope
        ? payload.queryScope
        : payload.query
        ? true
        : false;

      // Clear blue lens data if loading the drill down for a different entity
      if (state.entityDrillDownConfig.entityId[0] !== payload.entityId[0]) {
        state.entityBlueLensData = null;
      }
      // If no tab is defined in the payload, set to the default tab
      if (!payload.tab) {
        payload.tab = 'documents';
      }

      // Spread prev state so we can just pass updated properties instead of entire object
      state.entityDrillDownConfig = {
        ...state.entityDrillDownConfig,
        ...payload,
      };
    }
  },
  setMetaDataLoading(state, payload) {
    state.metaDataLoading = payload;
  },
  setAnalyticsLoading(state, payload) {
    state.analyticsLoading = payload;
  },
  setAssociationsLoading(state, payload) {
    state.associationsLoading = payload;
  },
  updateHistory(state, payload) {
    // If no payload, i.e. the drill down is closed, clear the entityDrillDownHistory
    if (!payload) {
      state.entityDrillDownHistory = [];
    } else {
      // Add the payload to history

      // Get the index of the current entity config in the history array
      const currHistoryIndex = state.entityDrillDownConfig.historyIndex;
      // Get the current entity config object from the history array
      const currEntity = state.entityDrillDownHistory[currHistoryIndex];
      // If the payload entity is the same as the currently loaded entity, update the history entry with the new payload
      if (
        currEntity &&
        currEntity.entityId[0] === payload.entityId[0] &&
        currEntity.entityType === payload.entityType &&
        payload.entityId.length === currEntity.entityId.length
      ) {
        // Update the history entry with the new payload
        state.entityDrillDownHistory[currHistoryIndex] = {
          ...state.entityDrillDownHistory[currHistoryIndex],
          ...payload,
        };
      } else {
        // Add the payload to history (Emulates browser history) Remove all configs from history that come after the currently-loaded config. This is to handle the case where the user goes back in history and then clicks a new entity to drill down, resulting in a new history path from that point forward. Another option is to splice and add the payload at currHistoryIndex + 1, altering history rather than creating a new path.
        state.entityDrillDownHistory.splice(currHistoryIndex + 1);
        // Set the payload historyIndex to the length of the history array, i.e. the index of the payload we're about to push
        payload.historyIndex = state.entityDrillDownHistory.length;
        // Push the payload to the history array
        state.entityDrillDownHistory.push(payload);
      }
    }
  },
  // Merge v2 drilldown state (drilldownFilters, drilldownTab) into the current history entry so Back/Forward restore it
  updateCurrentHistoryEntry(state, payload) {
    const index = state.entityDrillDownConfig.historyIndex;
    if (index != null && state.entityDrillDownHistory[index]) {
      state.entityDrillDownHistory.splice(index, 1, {
        ...state.entityDrillDownHistory[index],
        ...payload,
      });
    }
  },
  setEntityDocumentFilters(state, payload) {
    if (!payload) {
      Vue.set(state.entityDrillDownConfig, 'documentFilters', {});
    } else {
      Vue.set(state.entityDrillDownConfig, 'documentFilters', payload);
    }
  },
  setEntityNetworkGraphData(state, payload) {
    state.entityNetworkGraphData = payload;
  },
  setNetworkGraphDataLoading(state, payload) {
    state.networkGraphDataLoading = payload;
  },
  setEntityDrillDownTab(state, payload) {
    Vue.set(state.entityDrillDownConfig, 'tab', payload);
  },
  setShowAdvisories(state, payload) {
    Vue.set(state.entityDrillDownConfig, 'showAdvisories', payload);
  },
  setEntityBlueLensData(state, payload) {
    state.entityBlueLensData = payload;
  },
  setBlueLensDataLoading(state, payload) {
    state.blueLensDataLoading = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
